/** Nhập / xuất bệnh nhân hàng loạt (Excel). Real /api/v1/patients/import. */
import * as XLSX from "xlsx";
import { apiClient } from "./client";

export interface ImportRow {
  fullName: string;
  gender?: string | null;
  /** ISO yyyy-MM-dd. */
  dateOfBirth?: string | null;
  phone?: string | null;
  email?: string | null;
  addressLine?: string | null;
  /** Phường/Xã — free-text name or geo code. */
  wardCode?: string | null;
  /** Tỉnh/Thành — free-text name or geo code. */
  provinceCode?: string | null;
  /** Tên thân nhân. */
  guardianName?: string | null;
}

export interface ImportResult {
  total: number;
  created: number;
  errors: { index: number; fullName: string; error: string }[];
}

export const patientImportApi = {
  import(rows: ImportRow[]): Promise<ImportResult> {
    return apiClient
      .post<ImportResult>("/v1/patients/import", { rows })
      .then((r) => r.data);
  },
};

/**
 * The canonical column order — matches the sample export the clinic uses. Reused for the
 * downloadable template AND the patient export so the round-trip (tải mẫu → sửa → up lại) lines up.
 */
export const TEMPLATE_HEADERS = [
  "Họ & Tên",
  "Tên thân nhân",
  "Email",
  "Số ĐT",
  "Ngày sinh",
  "Địa chỉ",
  "Phường/Xã",
  "Tỉnh thành",
] as const;

/** Strip Vietnamese diacritics + punctuation for tolerant header matching. */
function norm(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

const ALIASES: Record<keyof ImportRow, string[]> = {
  fullName: ["hoten", "hovaten", "fullname", "name", "ten"],
  guardianName: ["tenthannhan", "thannhan", "nguoibaoho", "guardian"],
  email: ["email", "thudientu"],
  phone: ["sodt", "sodienthoai", "sdt", "phone", "dienthoai", "dt"],
  dateOfBirth: ["ngaysinh", "namsinh", "dob", "dateofbirth"],
  addressLine: ["diachi", "address"],
  wardCode: ["phuongxa", "phuong", "xa", "ward"],
  provinceCode: ["tinhthanh", "tinhthanhpho", "province", "tinh", "thanhpho"],
  gender: ["gioitinh", "gender", "phai"],
};

/**
 * Parse an uploaded workbook (.xls / .xlsx / .csv) into import rows. Header matching is tolerant of
 * Vietnamese diacritics + punctuation. Excel date serials AND text dates are both normalised to ISO.
 * Returns null if a "Họ & Tên" column cannot be located.
 */
export async function parseWorkbook(file: File): Promise<ImportRow[] | null> {
  // CSV: read as UTF-8 text (SheetJS mis-decodes UTF-8 CSV from an ArrayBuffer). Binary .xls/.xlsx:
  // read the bytes and let SheetJS handle the workbook codepage + date cells.
  const isCsv = /\.csv$/i.test(file.name) || file.type === "text/csv";
  const wb = isCsv
    ? XLSX.read(await file.text(), { type: "string" })
    : XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: true });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) return null;
  const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: false });
  if (grid.length < 2) return null;

  const header = (grid[0] as unknown[]).map((h) => norm(String(h ?? "")));
  const col = (key: keyof ImportRow) =>
    header.findIndex((h) => ALIASES[key].includes(h));
  const iName = col("fullName");
  if (iName < 0) return null;
  const idx = {
    guardianName: col("guardianName"),
    email: col("email"),
    phone: col("phone"),
    dateOfBirth: col("dateOfBirth"),
    addressLine: col("addressLine"),
    wardCode: col("wardCode"),
    provinceCode: col("provinceCode"),
    gender: col("gender"),
  };

  const cell = (row: unknown[], i: number): string =>
    i >= 0 && row[i] != null ? String(row[i]).trim() : "";

  const rows: ImportRow[] = [];
  for (let r = 1; r < grid.length; r++) {
    const row = grid[r] as unknown[];
    const fullName = cell(row, iName);
    if (!fullName) continue;
    rows.push({
      fullName,
      guardianName: cell(row, idx.guardianName) || null,
      email: cell(row, idx.email) || null,
      phone: cell(row, idx.phone) || null,
      dateOfBirth: toIsoDate(idx.dateOfBirth >= 0 ? row[idx.dateOfBirth] : null),
      addressLine: cell(row, idx.addressLine) || null,
      wardCode: cell(row, idx.wardCode) || null,
      provinceCode: cell(row, idx.provinceCode) || null,
      gender: cell(row, idx.gender) || null,
    });
  }
  return rows;
}

/** Download a blank Excel template (headers + 2 example rows) for the user to fill and re-upload. */
export function downloadTemplate(): void {
  const example = [
    ["Nguyễn Văn An", "", "", "0901234567", "1990-01-15", "12 Nguyễn Huệ", "Phường Bến Nghé", "Thành phố Hồ Chí Minh"],
    ["Trần Thị Bình", "Nguyễn Văn An (cha)", "binh@email.vn", "0912345678", "1985-06-20", "45 Lê Lợi", "Phường Giảng Võ", "Thành phố Hà Nội"],
  ];
  const ws = XLSX.utils.aoa_to_sheet([[...TEMPLATE_HEADERS], ...example]);
  ws["!cols"] = TEMPLATE_HEADERS.map(() => ({ wch: 20 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Bệnh nhân");
  XLSX.writeFile(wb, "mau-nhap-benh-nhan.xlsx");
}

/** Rows already in the 8-column shape used for export. */
export type ExportRow = (string | number | null | undefined)[];

/** Build + download an .xlsx of the given patient rows in the canonical column order. */
export function downloadPatientsXlsx(
  rows: ExportRow[],
  filename = "danh-sach-benh-nhan.xlsx"
): void {
  const ws = XLSX.utils.aoa_to_sheet([[...TEMPLATE_HEADERS], ...rows]);
  ws["!cols"] = TEMPLATE_HEADERS.map(() => ({ wch: 20 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Bệnh nhân");
  XLSX.writeFile(wb, filename);
}

/**
 * Build the import-result workbook (#18541): the original imported rows in their
 * original order + two appended columns "Kết quả import" (Thành công/Thất bại) and
 * "Lý do lỗi" (the per-row backend message — never a raw exception). Filename carries
 * a timestamp so it's distinguishable from the source file.
 */
export function downloadImportResult(
  rows: ImportRow[],
  result: ImportResult
): void {
  const errByIndex = new Map(result.errors.map((e) => [e.index, e.error]));
  const headers = [...TEMPLATE_HEADERS, "Kết quả import", "Lý do lỗi"];
  const aoa: (string | null | undefined)[][] = [headers];
  rows.forEach((r, i) => {
    const err = errByIndex.get(i);
    aoa.push([
      r.fullName,
      r.guardianName ?? "",
      r.email ?? "",
      r.phone ?? "",
      r.dateOfBirth ?? "",
      r.addressLine ?? "",
      r.wardCode ?? "",
      r.provinceCode ?? "",
      err ? "Thất bại" : "Thành công",
      err ?? "",
    ]);
  });
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = headers.map((_, i) => ({
    wch: i >= TEMPLATE_HEADERS.length ? 30 : 20,
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Kết quả nhập");
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(
    d.getHours()
  )}${p(d.getMinutes())}${p(d.getSeconds())}`;
  XLSX.writeFile(wb, `ket-qua-import-benh-nhan-${stamp}.xlsx`);
}

/** Excel serial number, JS Date, or a text date → ISO yyyy-MM-dd (or null). */
function toIsoDate(v: unknown): string | null {
  if (v == null || v === "") return null;
  if (v instanceof Date) return isoOf(v.getFullYear(), v.getMonth() + 1, v.getDate());
  if (typeof v === "number") {
    const d = XLSX.SSF.parse_date_code(v);
    return d ? isoOf(d.y, d.m, d.d) : null;
  }
  const t = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) return isoOf(Number(m[3]), Number(m[2]), Number(m[1]));
  return null;
}

function isoOf(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
